/**
 * 検査ロジック管理フック
 *
 * 線分二等分課題の検査状態と試行の進行を管理
 */

import { useState, useCallback, useMemo } from 'react';
import {
  ExamState,
  ExamPhase,
  ExamSettings,
  Patient,
  TrialData,
  LineInfo,
  MultiLineInfo,
  ExamResultSummary,
} from '../types';
const DEFAULT_EXAM_SETTINGS = {
  TRIAL_COUNT: 5,
  LINE_LENGTHS: [80, 100, 120],
  LINE_THICKNESS: 0.5,
  ABNORMAL_THRESHOLD: 5,
  INPUT_MODE: 'touch' as const,
  SHOW_PROGRESS_BAR: true,
} as const;
import {
  getRandomLinePosition,
  calculateLineEndpoints,
  calculateDeviation,
  isAbnormal,
  generateMultiLinePositions,
} from '../utils/canvas';
import {
  getCalibration,
  mmToPx,
  pxToMm,
  getCanvasDimensionsMm,
} from '../utils/calibration';

/**
 * useExam フックの戻り値
 */
interface UseExamResult {
  /** 検査状態 */
  state: ExamState;
  /** 検査を初期化 */
  initializeExam: (patient: Patient, settings: ExamSettings) => void;
  /** 検査を開始 */
  startExam: (canvasWidth: number, canvasHeight: number) => void;
  /** マーク位置を記録（単一線分モード） */
  recordMark: (xPx: number, yPx: number) => void;
  /** マーク位置を記録（複数線分モード：特定の線分にマーク） */
  recordMultiLineMark: (lineIndex: number, xPx: number, yPx: number) => void;
  /** 次の試行へ進む */
  nextTrial: (canvasWidth: number, canvasHeight: number) => void;
  /** 検査をキャンセル */
  cancelExam: () => void;
  /** 検査結果サマリーを取得 */
  getResultSummary: () => ExamResultSummary | null;
  /** 進捗率（0-100） */
  progress: number;
  /** 完了したかどうか */
  isCompleted: boolean;
  /** キャリブレーション済みかどうか */
  isCalibrated: boolean;
  /** 複数線分モードかどうか */
  isMultiLineMode: boolean;
}

/**
 * 初期状態
 */
const initialState: ExamState = {
  phase: 'idle',
  settings: {
    trialCount: DEFAULT_EXAM_SETTINGS.TRIAL_COUNT,
    lineLengths: [...DEFAULT_EXAM_SETTINGS.LINE_LENGTHS],
    lineThickness: DEFAULT_EXAM_SETTINGS.LINE_THICKNESS,
    abnormalThreshold: DEFAULT_EXAM_SETTINGS.ABNORMAL_THRESHOLD,
    inputMode: DEFAULT_EXAM_SETTINGS.INPUT_MODE,
    showProgressBar: DEFAULT_EXAM_SETTINGS.SHOW_PROGRESS_BAR,
  },
  patient: null,
  currentTrialNumber: 0,
  trialResults: [],
  currentLine: null,
  multiLines: null,
  error: null,
};

/**
 * 検査管理フック
 */
export function useExam(): UseExamResult {
  const [state, setState] = useState<ExamState>(initialState);

  // キャリブレーション確認
  const calibration = useMemo(() => getCalibration(), []);
  const isCalibrated = useMemo(() => !!calibration.calibratedAt, [calibration]);

  /**
   * 試行で使用する線分長を取得（ランダムモード用）
   */
  const getLineLengthForTrial = useCallback((): number => {
    const { lineLengths } = state.settings;
    return lineLengths[Math.floor(Math.random() * lineLengths.length)];
  }, [state.settings]);

  /**
   * 指定試行の線の長さ配列を取得
   * trialPlan がある場合は lineLengthMode に関係なく優先して使用する。
   */
  const getTrialPlanLengths = useCallback((trialIndex: number): number[] | null => {
    const { trialPlan } = state.settings;
    if (!trialPlan || trialPlan.length === 0) return null;
    if (trialIndex < trialPlan.length) return trialPlan[trialIndex];
    return trialPlan[trialIndex % trialPlan.length];
  }, [state.settings]);

  /**
   * 試行ごとの線分本数を取得
   * trialPlan があればその行の本数を優先し、なければ従来設定を使用する。
   */
  const getLineCountForTrial = useCallback((trialIndex: number): number => {
    const planLengths = getTrialPlanLengths(trialIndex);
    if (planLengths && planLengths.length > 0) {
      return planLengths.length;
    }
    if (state.settings.multiLineMode) {
      return Math.max(1, state.settings.multiLineCount ?? 2);
    }
    return 1;
  }, [getTrialPlanLengths, state.settings.multiLineMode, state.settings.multiLineCount]);

  /**
   * 新しい線分情報を生成
   * trialIndex: customモードで試行番号から長さを取得するために使用
   */
  const generateLineInfo = useCallback(
    (canvasWidthPx: number, canvasHeightPx: number, trialIndex?: number): LineInfo => {
      const { widthMm, heightMm } = getCanvasDimensionsMm(
        canvasWidthPx,
        canvasHeightPx
      );

      // customモードの場合はtrialPlanから最初の長さを使用（単一線分モード）
      const planLengths = trialIndex !== undefined ? getTrialPlanLengths(trialIndex) : null;
      const lengthMm = planLengths && planLengths.length > 0
        ? planLengths[0]
        : getLineLengthForTrial();

      const posMode = state.settings.linePositionMode || 'random';
      const marginMm = 20;
      const halfLen = lengthMm / 2;
      const safeMinX = marginMm + halfLen;
      const safeMaxX = widthMm - marginMm - halfLen;
      let centerXMm: number;
      let centerYMm: number;
      if (posMode === 'center') {
        centerXMm = widthMm / 2;
        centerYMm = heightMm / 2;
      } else if (posMode === 'left') {
        // 左端〜画面幅の25%の範囲
        const rangeMax = Math.min(safeMinX + (safeMaxX - safeMinX) * 0.25, safeMaxX);
        centerXMm = safeMinX + Math.random() * (rangeMax - safeMinX);
        centerYMm = marginMm + Math.random() * (heightMm - 2 * marginMm);
      } else if (posMode === 'right') {
        // 画面幅の75%〜右端の範囲
        const rangeMin = Math.max(safeMinX + (safeMaxX - safeMinX) * 0.75, safeMinX);
        centerXMm = rangeMin + Math.random() * (safeMaxX - rangeMin);
        centerYMm = marginMm + Math.random() * (heightMm - 2 * marginMm);
      } else {
        const pos = getRandomLinePosition(widthMm, heightMm, lengthMm);
        centerXMm = pos.x;
        centerYMm = pos.y;
      }

      const { x1, y1, x2, y2 } = calculateLineEndpoints(
        centerXMm,
        centerYMm,
        lengthMm
      );

      // mm から px に変換
      const ppmm = calibration.ppmm;
      return {
        centerXMm,
        centerYMm,
        lengthMm,
        x1Px: mmToPx(x1, ppmm),
        y1Px: mmToPx(y1, ppmm),
        x2Px: mmToPx(x2, ppmm),
        y2Px: mmToPx(y2, ppmm),
      };
    },
    [calibration.ppmm, getLineLengthForTrial, getTrialPlanLengths, state.settings]
  );

  /**
   * 複数線分情報を生成
   */
  const generateMultiLineInfos = useCallback(
    (canvasWidthPx: number, canvasHeightPx: number, lineCount: number, trialIndex?: number): MultiLineInfo[] => {
      const { widthMm, heightMm } = getCanvasDimensionsMm(
        canvasWidthPx,
        canvasHeightPx
      );

      // 各線分の長さを決定
      const { lineLengths } = state.settings;
      const planLengths = trialIndex !== undefined ? getTrialPlanLengths(trialIndex) : null;

      const lengths = planLengths && planLengths.length > 0
        ? planLengths
        : Array.from({ length: lineCount }, () =>
            lineLengths[Math.floor(Math.random() * lineLengths.length)]
          );

      // 配置パターンに応じた位置を生成
      const posMode = state.settings.linePositionMode || 'random';
      const positions = generateMultiLinePositions(widthMm, heightMm, lengths, 20, 15, posMode);

      const ppmm = calibration.ppmm;
      return positions.map((pos, i) => {
        const lengthMm = lengths[i];
        const { x1, y1, x2, y2 } = calculateLineEndpoints(pos.x, pos.y, lengthMm);

        return {
          index: i,
          centerXMm: pos.x,
          centerYMm: pos.y,
          lengthMm,
          x1Px: mmToPx(x1, ppmm),
          y1Px: mmToPx(y1, ppmm),
          x2Px: mmToPx(x2, ppmm),
          y2Px: mmToPx(y2, ppmm),
          marked: false,
          markedXPx: null,
          markedYPx: null,
        };
      });
    },
    [calibration.ppmm, state.settings]
  );

  /**
   * 検査を初期化
   */
  const initializeExam = useCallback(
    (patient: Patient, settings: ExamSettings) => {
      const normalizedTrialCount =
        settings.trialPlan && settings.trialPlan.length > 0
          ? settings.trialPlan.length
          : settings.trialCount;
      setState({
        ...initialState,
        phase: 'preparing',
        settings: {
          ...settings,
          trialCount: normalizedTrialCount,
          disableAbnormalJudgment: settings.disableAbnormalJudgment ?? false,
        },
        patient,
      });
    },
    []
  );

  /**
   * 検査を開始
   */
  const startExam = useCallback(
    (canvasWidth: number, canvasHeight: number) => {
      setState((prev) => {
        const lineCount = getLineCountForTrial(0);
        const isMulti = lineCount >= 2;

        if (isMulti) {
          const multiLines = generateMultiLineInfos(canvasWidth, canvasHeight, lineCount, 0);
          return {
            ...prev,
            phase: 'running' as ExamPhase,
            currentTrialNumber: 1,
            currentLine: null,
            multiLines,
            trialResults: [],
            error: null,
          };
        } else {
          const lineInfo = generateLineInfo(canvasWidth, canvasHeight, 0);
          return {
            ...prev,
            phase: 'running' as ExamPhase,
            currentTrialNumber: 1,
            currentLine: lineInfo,
            multiLines: null,
            trialResults: [],
            error: null,
          };
        }
      });
    },
    [generateLineInfo, generateMultiLineInfos, getLineCountForTrial]
  );

  /**
   * マーク位置を記録
   */
  const recordMark = useCallback(
    (xPx: number, yPx: number) => {
      if (state.phase !== 'running' || !state.currentLine) {
        return;
      }

      const ppmm = calibration.ppmm;
      const markedXMm = pxToMm(xPx, ppmm);
      const markedYMm = pxToMm(yPx, ppmm);

      const { deviationMm, deviationPercent } = calculateDeviation(
        markedXMm,
        state.currentLine.centerXMm,
        state.currentLine.lengthMm
      );

      const abnormal = state.settings.disableAbnormalJudgment
        ? false
        : isAbnormal(deviationPercent, state.settings.abnormalThreshold);

      const trialData: TrialData = {
        trialNumber: state.currentTrialNumber,
        lineLengthMm: state.currentLine.lengthMm,
        linePositionXMm: state.currentLine.centerXMm,
        linePositionYMm: state.currentLine.centerYMm,
        markedPositionXMm: markedXMm,
        markedPositionYMm: markedYMm,
        deviationMm,
        deviationPercent,
        isAbnormal: abnormal,
        timestamp: new Date().toISOString(),
      };

      const newTrialResults = [...state.trialResults, trialData];
      const isLastTrial =
        state.currentTrialNumber >= state.settings.trialCount;

      setState((prev) => ({
        ...prev,
        phase: isLastTrial ? 'completed' : 'waiting',
        trialResults: newTrialResults,
      }));
    },
    [state, calibration.ppmm]
  );

  /**
   * マーク位置を記録（複数線分モード）
   * 特定のインデックスの線分にマークを記録する。
   * 全ての線分にマークされたら waiting 状態に遷移し、TrialData を生成する。
   */
  const recordMultiLineMark = useCallback(
    (lineIndex: number, xPx: number, yPx: number) => {
      setState((prev) => {
        if (prev.phase !== 'running' || !prev.multiLines) {
          return prev;
        }

        const targetLine = prev.multiLines[lineIndex];
        if (!targetLine || targetLine.marked) {
          return prev;
        }

        // マーク状態を更新
        const updatedLines = prev.multiLines.map((line, i) =>
          i === lineIndex
            ? { ...line, marked: true, markedXPx: xPx, markedYPx: yPx }
            : line
        );

        const allMarked = updatedLines.every((line) => line.marked);
        if (!allMarked) {
          return {
            ...prev,
            multiLines: updatedLines,
          };
        }

        const isLastTrial = prev.currentTrialNumber >= prev.settings.trialCount;
        const ppmm = calibration.ppmm;
        const newTrialDatas: TrialData[] = updatedLines.map((line) => {
          const markedXMm = pxToMm(line.markedXPx!, ppmm);
          const markedYMm = pxToMm(line.markedYPx!, ppmm);

          const { deviationMm, deviationPercent } = calculateDeviation(
            markedXMm,
            line.centerXMm,
            line.lengthMm
          );

          const abnormal = prev.settings.disableAbnormalJudgment
            ? false
            : isAbnormal(deviationPercent, prev.settings.abnormalThreshold);

          return {
            trialNumber: prev.currentTrialNumber,
            lineLengthMm: line.lengthMm,
            linePositionXMm: line.centerXMm,
            linePositionYMm: line.centerYMm,
            markedPositionXMm: markedXMm,
            markedPositionYMm: markedYMm,
            deviationMm,
            deviationPercent,
            isAbnormal: abnormal,
            timestamp: new Date().toISOString(),
          };
        });

        return {
          ...prev,
          phase: isLastTrial ? 'completed' : 'waiting',
          multiLines: updatedLines,
          trialResults: [...prev.trialResults, ...newTrialDatas],
        };
      });
    },
    [calibration.ppmm]
  );

  /**
   * 次の試行へ進む
   */
  const nextTrial = useCallback(
    (canvasWidth: number, canvasHeight: number) => {
      if (state.phase !== 'waiting') {
        return;
      }

      setState((prev) => {
        const nextTrialNum = prev.currentTrialNumber + 1;
        const trialIndex = nextTrialNum - 1; // 0-indexed
        const lineCount = getLineCountForTrial(trialIndex);
        const isMulti = lineCount >= 2;

        if (isMulti) {
          const multiLines = generateMultiLineInfos(canvasWidth, canvasHeight, lineCount, trialIndex);
          return {
            ...prev,
            phase: 'running' as ExamPhase,
            currentTrialNumber: nextTrialNum,
            currentLine: null,
            multiLines,
          };
        } else {
          const lineInfo = generateLineInfo(canvasWidth, canvasHeight, trialIndex);
          return {
            ...prev,
            phase: 'running' as ExamPhase,
            currentTrialNumber: nextTrialNum,
            currentLine: lineInfo,
            multiLines: null,
          };
        }
      });
    },
    [state.phase, generateLineInfo, generateMultiLineInfos, getLineCountForTrial]
  );

  /**
   * 検査をキャンセル
   */
  const cancelExam = useCallback(() => {
    setState((prev) => ({
      ...prev,
      phase: 'cancelled',
    }));
  }, []);

  /**
   * 検査結果サマリーを取得
   */
  const getResultSummary = useCallback((): ExamResultSummary | null => {
    if (state.trialResults.length === 0) {
      return null;
    }

    const deviationsMm = state.trialResults.map((t) => t.deviationMm);
    const deviationsPercent = state.trialResults.map((t) => t.deviationPercent);

    const totalTrials = state.trialResults.length;
    const abnormalTrials = state.trialResults.filter((t) => t.isAbnormal).length;

    const sumMm = deviationsMm.reduce((a, b) => a + b, 0);
    const averageDeviationMm = sumMm / totalTrials;

    const sumPercent = deviationsPercent.reduce((a, b) => a + b, 0);
    const averageDeviationPercent = sumPercent / totalTrials;

    // 標準偏差
    const squaredDiffs = deviationsMm.map((d) =>
      Math.pow(d - averageDeviationMm, 2)
    );
    const variance = squaredDiffs.reduce((a, b) => a + b, 0) / totalTrials;
    const stdDeviationMm = Math.sqrt(variance);

    const maxDeviationMm = Math.max(...deviationsMm);
    const minDeviationMm = Math.min(...deviationsMm);

    return {
      totalTrials,
      abnormalTrials,
      averageDeviationMm: Math.round(averageDeviationMm * 100) / 100,
      averageDeviationPercent: Math.round(averageDeviationPercent * 100) / 100,
      stdDeviationMm: Math.round(stdDeviationMm * 100) / 100,
      maxDeviationMm: Math.round(maxDeviationMm * 100) / 100,
      minDeviationMm: Math.round(minDeviationMm * 100) / 100,
    };
  }, [state.trialResults]);

  // 複数線分モードかどうか
  const isMultiLineMode = !!(state.multiLines && state.multiLines.length >= 2);

  // 進捗率
  // 複数線分モードの場合、trialResultsの数は試行数 * 線分数なので、
  // 完了した試行数ベースで計算する
  const progress = useMemo(() => {
    if (state.settings.trialCount === 0) return 0;
    if (state.phase === 'completed') return 100;

    const completedTrials =
      state.phase === 'waiting'
        ? state.currentTrialNumber
        : Math.max(0, state.currentTrialNumber - 1);

    return Math.round((completedTrials / state.settings.trialCount) * 100);
  }, [state.phase, state.currentTrialNumber, state.settings.trialCount]);

  // 完了フラグ
  const isCompleted = state.phase === 'completed';

  return {
    state,
    initializeExam,
    startExam,
    recordMark,
    recordMultiLineMark,
    nextTrial,
    cancelExam,
    getResultSummary,
    progress,
    isCompleted,
    isCalibrated,
    isMultiLineMode,
  };
}

export default useExam;
